// Period comparison shared by the profit and loss, balance sheet, cash summary and executive
// summary: which columns to fetch (one API call each) and how a row with a variance is built.
import { useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useTenant } from '../../auth/session';
import { varianceCells, type Cell, type Column, type ReportRow } from './model';
import { addMonths, isMonthEnd, monthEnd, previousPeriod, rangeLabel, samePeriodLastYear, type Range } from './period';
import { fmtDate } from '../../lib/format';

/**
 * One query per comparison column, keyed by tenant like useApiQuery, so the number of columns can
 * change without breaking the rules of hooks.
 */
export function useApiQueries<T>(specs: { key: readonly unknown[]; fn: () => Promise<T>; enabled?: boolean }[]) {
  const tenant = useTenant();
  return useQueries({ queries: specs.map((s) => ({ queryKey: [tenant.id, ...s.key], queryFn: s.fn, enabled: s.enabled ?? true })) });
}

/** Loading and error state over several queries, for ReportFrame. */
export const stateOf = (...qs: { isLoading: boolean; error: unknown; refetch: () => unknown }[]) => ({
  loading: qs.some((q) => q.isLoading),
  error: qs.find((q) => q.error)?.error ?? null,
  retry: () => { for (const q of qs) void q.refetch(); },
});

export type Compare = 'none' | 'previous' | 'last-year' | 'both';
export const COMPARE: [Compare, string][] = [['none', 'No comparison'], ['previous', 'Previous period'], ['last-year', 'Same period last year'], ['both', 'Previous period and last year']];

/** The current range first, then the ranges it is compared with. */
export function rangeColumns(range: Range, compare: Compare): { label: string; range: Range }[] {
  const cols = [{ label: rangeLabel(range), range }];
  if (compare === 'previous' || compare === 'both') { const r = previousPeriod(range); cols.push({ label: rangeLabel(r), range: r }); }
  if (compare === 'last-year' || compare === 'both') { const r = samePeriodLastYear(range); cols.push({ label: rangeLabel(r), range: r }); }
  return cols;
}

export type AsOfCompare = 'none' | 'month' | 'quarter' | 'year' | 'months';
export const AS_OF_COMPARE: [AsOfCompare, string][] = [
  ['none', 'No comparison'], ['month', 'Previous month end'], ['quarter', 'Previous quarter end'], ['year', 'Same date last year'], ['months', 'Previous 3 month ends'],
];
/** The same point n months earlier: a month end stays a month end (Mar 31 → Feb 28). */
export const monthsBefore = (asOf: string, n: number) => (isMonthEnd(asOf) ? monthEnd(addMonths(asOf, -n)) : addMonths(asOf, -n));
export function asOfColumns(asOf: string, compare: AsOfCompare): { label: string; asOf: string }[] {
  const steps = { none: [], month: [1], quarter: [3], year: [12], months: [1, 2, 3] }[compare];
  return [asOf, ...steps.map((n) => monthsBefore(asOf, n))].map((d) => ({ label: fmtDate(d), asOf: d }));
}

/** The comparison as the export dialog describes it ("Compared with previous period"), or nothing. */
export function compareDetail<T extends string>(options: [T, string][], value: T): string | undefined {
  const label = options.find(([k]) => k === value)?.[1];
  return value === 'none' || !label ? undefined : `Compared with ${label.charAt(0).toLowerCase()}${label.slice(1)}`;
}
/** The compare query parameter for a report PDF: left out when there is no comparison. */
export const compareParam = (value: string) => (value === 'none' ? null : value);

/** Column headings: the label column, one per period, then Variance and its arrows when comparing. */
export function compareColumns(labels: string[]): Column[] {
  return [{ label: '' }, ...labels.map((label) => ({ label, align: 'r' as const })), ...(labels.length > 1 ? [{ label: 'Variance', align: 'r' as const }, { label: 'Direction', arrows: true }] : [])];
}

/**
 * One statement row across the columns. `nums` are the comparable values (null when a column
 * has none); the variance compares the first column with the second.
 */
export function compareRow(kind: ReportRow['kind'], label: string, cells: Cell[], nums: (bigint | null)[], good: 'up' | 'down' | null, extra: Partial<ReportRow> = {}): ReportRow {
  const variance = cells.length > 1 ? varianceCells(nums[0] ?? null, nums[1] ?? null, good) : [];
  return { kind, label, cells: [...cells, ...variance], ...extra };
}

/** "Variance compares Oct 2026 with Sep 2026. Green arrows are better, red worse, grey neither." */
export const varianceNote = (labels: string[]) =>
  labels.length > 1 ? `Variance compares ${labels[0]} with ${labels[1]}. Green arrows are better, red worse, grey neither.` : 'Choose a comparison to see how the period moved.';

export function CompareSelect<T extends string>({ value, options, onChange, disabled }: { value: T; options: [T, string][]; onChange: (v: T) => void; disabled?: boolean }) {
  return (
    <div className="field">
      <label htmlFor="rp-compare">Compare with</label>
      <select className="input" id="rp-compare" style={{ width: 'auto' }} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value as T)}>
        {options.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
    </div>
  );
}

export function CompactSwitch({ compact, setCompact }: { compact: boolean; setCompact: (f: (c: boolean) => boolean) => void }) {
  return <button type="button" className="switch" role="switch" aria-checked={compact} onClick={() => setCompact((c) => !c)}><span className="knob" />Compact view</button>;
}
export const useCompact = () => useState(false);
